import { Component, forwardRef, OnInit, OnDestroy } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Observable, timer, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { SmsVerificationService } from '../../../core/services/sms-verification.service';
import { CookieService } from 'ngx-cookie-service';

export interface SmsVerificationData {
  phoneNumber: string;
  isVerified: boolean;
}

@Component({
  selector: 'app-sms-verification',
  templateUrl: './sms-verification.component.html',
  styleUrls: ['./sms-verification.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SmsVerificationComponent),
      multi: true,
    },
  ],
})
export class SmsVerificationComponent implements ControlValueAccessor, OnInit, OnDestroy {
  public phoneForm: FormGroup;
  public verificationForm: FormGroup;
  public currentStep: 'phone' | 'verification' = 'phone';
  public isVerified: boolean = false;
  public disabled: boolean = false;
  public isLoading: boolean = false;
  public error: string = '';
  public resendCountdown$: Observable<number> | null = null;
  public canResend: boolean = true;
  public isCheckingSession = true;

  private readonly VERIFICATION_STORAGE_KEY = 'sms_verification_session';

  private destroy$ = new Subject<void>();
  private countryCode = '+1';
  private readonly COUNTRY_CODE_COOKIE_KEY = 'country_code';

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private onChange = (value: SmsVerificationData) => {};
  private onTouched = () => {};

  constructor(
    private formBuilder: FormBuilder,
    private smsVerificationService: SmsVerificationService,
    private cookieService: CookieService,
  ) {
    this.phoneForm = this.formBuilder.group({
      phoneNumber: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
    });

    this.verificationForm = this.formBuilder.group({
      code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });

    const cookie = this.cookieService.get(this.COUNTRY_CODE_COOKIE_KEY);
    if (cookie && cookie.trim()) {
      this.countryCode = cookie;
    }
  }

  async ngOnInit(): Promise<void> {
    await this.checkExistingSession();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Check if there's an existing valid verification session
   */
  private async checkExistingSession(): Promise<void> {
    const sessionToken = sessionStorage.getItem(this.VERIFICATION_STORAGE_KEY);

    if (sessionToken) {
      try {
        const response = await this.smsVerificationService
          .validateSession(sessionToken)
          .toPromise();

        if (response?.valid && response.phoneNumber) {
          // Session is valid, mark as verified
          this.isVerified = true;
          this.emitValue(response.phoneNumber);
          return;
        } else {
          // Session is invalid on backend, remove from storage
          this.clearSession();
        }
      } catch (error) {
        console.error('Session validation error:', error);
        // Remove invalid session
        this.clearSession();
      }
    }
    this.isCheckingSession = false;
  }

  /**
   * Store the session token in session storage
   */
  private storeSessionToken(sessionToken: string): void {
    sessionStorage.setItem(this.VERIFICATION_STORAGE_KEY, sessionToken);
  }

  async sendVerificationCode(): Promise<void> {
    if (this.phoneForm.invalid) {
      this.markFormGroupTouched(this.phoneForm);
      return;
    }

    this.isLoading = true;
    this.error = '';

    try {
      const { phoneNumber } = this.phoneForm.value;
      const fullPhoneNumber = `${this.countryCode}${phoneNumber}`;

      const response = await this.smsVerificationService
        .sendVerificationCode(fullPhoneNumber)
        .toPromise();

      if (response?.success) {
        this.currentStep = 'verification';
        this.startResendCountdown();
      } else {
        this.error = response?.message || 'Failed to send verification code. Please try again.';
      }
    } catch (error: any) {
      console.error('SMS verification error:', error);
      this.error = error?.error?.message || 'Failed to send verification code. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  async verifyCode(): Promise<void> {
    if (this.verificationForm.invalid) {
      this.markFormGroupTouched(this.verificationForm);
      return;
    }

    this.isLoading = true;
    this.error = '';

    try {
      const { code } = this.verificationForm.value;
      const { phoneNumber } = this.phoneForm.value;
      const fullPhoneNumber = `${this.countryCode}${phoneNumber}`;

      const response = await this.smsVerificationService
        .verifyCode(fullPhoneNumber, code)
        .toPromise();

      if (response?.success && response?.verified) {
        this.isVerified = true;

        // Store session token in sessionStorage if provided
        if (response.sessionToken) {
          this.storeSessionToken(response.sessionToken);
        }

        this.emitValue();
      } else {
        this.error = response?.message || 'Invalid verification code. Please try again.';
      }
    } catch (error: any) {
      console.error('Code verification error:', error);
      this.error = error?.error?.message || 'Verification failed. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  async resendCode(): Promise<void> {
    if (!this.canResend) return;

    this.isLoading = true;
    this.error = '';

    try {
      const { phoneNumber } = this.phoneForm.value;
      const fullPhoneNumber = `${this.countryCode}${phoneNumber}`;

      const response = await this.smsVerificationService
        .sendVerificationCode(fullPhoneNumber)
        .toPromise();

      if (response?.message) {
        this.startResendCountdown();
      } else {
        this.error = response?.message || 'Failed to resend verification code. Please try again.';
      }
    } catch (error: any) {
      console.error('Resend SMS error:', error);
      this.error = error?.error?.message || 'Failed to resend verification code. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  editPhoneNumber(): void {
    this.clearSession();
  }

  /**
   * Clear the verification session and storage
   */
  clearSession(): void {
    sessionStorage.removeItem(this.VERIFICATION_STORAGE_KEY);

    // Reset component state
    this.isVerified = false;
    this.currentStep = 'phone';
    this.error = '';
    this.phoneForm.reset();
    this.verificationForm.reset();
  }

  /**
   * Check if user has a valid verification session
   */
  public hasValidSession(): boolean {
    return this.isVerified;
  }

  /**
   * Get the current verification status
   */
  public getVerificationData(): SmsVerificationData {
    return {
      phoneNumber: this.isVerified ? `${this.countryCode}${this.phoneForm.value.phoneNumber}` : '',
      isVerified: this.isVerified,
    };
  }

  private startResendCountdown(): void {
    this.canResend = false;
    this.resendCountdown$ = timer(0, 1000).pipe(
      map((i) => 60 - i),
      map((remaining) => {
        if (remaining <= 0) {
          this.canResend = true;
          return 0;
        }
        return remaining;
      }),
    );
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private emitValue(phoneNumber?: string): void {
    const finalPhoneNumber =
      phoneNumber ||
      (this.isVerified ? `${this.countryCode}${this.phoneForm.value.phoneNumber}` : '');
    const value: SmsVerificationData = {
      phoneNumber: finalPhoneNumber,
      isVerified: this.isVerified,
    };
    this.onChange(value);
    this.onTouched();
  }

  getPhoneError(): string {
    const phoneControl = this.phoneForm.get('phoneNumber');
    if (phoneControl?.hasError('required') && phoneControl?.touched) {
      return 'Phone number is required';
    }
    if (phoneControl?.hasError('pattern') && phoneControl?.touched) {
      return 'Please enter a valid 10-digit phone number';
    }
    return '';
  }

  getVerificationError(): string {
    const codeControl = this.verificationForm.get('code');
    if (codeControl?.hasError('required') && codeControl?.touched) {
      return 'Verification code is required';
    }
    if (codeControl?.hasError('pattern') && codeControl?.touched) {
      return 'Please enter a valid 6-digit verification code';
    }
    return '';
  }

  // ControlValueAccessor implementation
  writeValue(value: SmsVerificationData): void {
    if (value) {
      this.isVerified = value.isVerified;
      if (value.phoneNumber && value.isVerified) {
        // Parse phone number if needed
        this.currentStep = 'verification';
      }
    }
  }

  registerOnChange(fn: (value: SmsVerificationData) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.phoneForm.disable();
      this.verificationForm.disable();
    } else {
      this.phoneForm.enable();
      this.verificationForm.enable();
    }
  }
}
